import { FC, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import MoveDownIcon from "@mui/icons-material/MoveDownOutlined";
import { isEmpty } from "lodash";
import useBasePath from "src/utils/hooks/useBasePath";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Title from "src/Pages/Company/Absences/Title/Title";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useListAbsenceTypes } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import { useFetchAbsenceGroups } from "src/Pages/Company/Absences/api/absenceGroups.queries";
import { useFilteredAbsenceTypes } from "src/utils/hooks/absences/useFilteredAbsenceTypes";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";
import transformAbsenceTypesToGroupedLists from "src/utils/Company/Absences/transformAbsenceTypesToGroupedLists";
import useAbsenceGroupSortingModal from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroupSorting/useAbsenceGroupSortingModal";
import AbsenceTypesListSearch from "./AbsenceTypesList/AbsenceTypesListSearch";
import AbsenceTypesList from "./AbsenceTypesList/AbsenceTypesList";
import AbsenceTypesListItemSkeleton from "./AbsenceTypesList/AbsenceTypesListItemSkeleton";

const AbsenceTypes: FC = () => {
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const logStatus = useLogStatus();

  const [activeTab, setActiveTab] = useState<"active" | "inactive">("active");
  const [searchQuery, setSearchQuery] = useState("");

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsenceTypes.tsx",
    });
  }

  useEffect(() => {
    setSearchQuery("");
  }, [activeTab]);

  const { data: absenceGroups } = useFetchAbsenceGroups(companyId);

  const {
    data: absenceTypesData,
    isFetching,
    isError,
    isSuccess,
  } = useListAbsenceTypes(companyId);

  const { openSortingModal } = useAbsenceGroupSortingModal({
    companyId,
    absenceGroups,
    absenceTypes: absenceTypesData,
  });

  const { active, inactive } = useMemo(
    () => transformAbsenceTypesToGroupedLists(absenceGroups, absenceTypesData),
    [absenceGroups, absenceTypesData]
  );

  const filteredActive = useFilteredAbsenceTypes(active, searchQuery);
  const filteredInactiveRaw = useFilteredAbsenceTypes(inactive, searchQuery);
  const filteredInactive = useMemo(
    () =>
      filteredInactiveRaw.map((group) => ({
        ...group,
        absenceTypes: [...group.absenceTypes].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      })),
    [filteredInactiveRaw]
  );
  const currentData =
    activeTab === "active" ? filteredActive : filteredInactive;
  const hasContent = !isEmpty(currentData);

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTab: "active" | "inactive"
  ) => {
    if (newTab !== null) {
      setActiveTab(newTab);
    }
  };

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: "Szablony wniosków",
          },
        ]}
      />
      <Title title={__("Szablony wniosków")} />
      <Stack
        direction="row"
        sx={{ alignItems: "center", mb: "1rem" }}
        spacing={2}
      >
        {canWriteAbsencesConfiguration && (
          <>
            {activeTab === "active" && (
              <Button
                variant="outlined"
                startIcon={<MoveDownIcon />}
                onClick={openSortingModal}
                disabled={!absenceTypesData}
                data-testid="absence-groups__button-change-sorting"
              >
                {__("Zmień kolejność")}
              </Button>
            )}
            <Button
              component={Link}
              variant="contained"
              to={`${basePath}/absences/absence-type/create`}
              sx={{ marginBottom: "auto" }}
              startIcon={<AddIcon />}
              data-testid="absence-types-create-button"
            >
              {__("Nowy szablon")}
            </Button>
          </>
        )}
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={handleTabChange}
          aria-label={__("Wybierz typ wymiaru")}
          sx={{
            marginLeft: "auto !important",
            "& .MuiToggleButton-root": {
              textTransform: "none",
            },
          }}
        >
          <ToggleButton value="active" aria-label={__("Szablony aktywne")}>
            {__("Aktywne")}
          </ToggleButton>
          <ToggleButton value="inactive" aria-label={__("Szablony nieaktywne")}>
            {__("Nieaktywne")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {!canWriteAbsencesConfiguration && <PermissionsAlert />}

      {isSuccess && absenceTypesData && absenceTypesData.length > 0 && (
        <AbsenceTypesListSearch
          isFetching={isFetching}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      )}

      {isError && <ErrorDataFetching />}

      {searchQuery && !hasContent && (
        <Typography variant="subtitle1" sx={{ textAlign: "center", mt: 4 }}>
          {`${__("Brak wyników wyszukiwania dla")} "${searchQuery}"`}
        </Typography>
      )}

      <Box sx={{ display: activeTab === "active" ? "block" : "none" }}>
        {isFetching ? (
          <AbsenceTypesListItemSkeleton />
        ) : (
          <AbsenceTypesList
            label="Aktywne szablony"
            data={filteredActive}
            disabled={isFetching}
          />
        )}
      </Box>

      <Box sx={{ display: activeTab === "inactive" ? "block" : "none" }}>
        <AbsenceTypesList
          label="Nieaktywne szablony"
          data={filteredInactive}
          disabled={isFetching}
        />
      </Box>
    </>
  );
};

export default AbsenceTypes;
